// File: src/components/navigation/sidebar-nav.tsx
// Purpose: Nested navigation sidebar component for VIBE platform
// VIBE - Sidebar Navigation Component


import { useState, useEffect, useCallback, useMemo, JSX } from 'react'
import { Link } from 'react-router-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ChevronDown,
  ChevronRight,
  Circle,
  LayoutDashboard,
  BarChart3,
  FolderKanban,
  Users,
  Bell,
  Settings,
  Sparkles,
  Code,
  CheckSquare,
  UserCheck,
  Building,
  CreditCard,
  Plug,
  Rocket,
  Shield,
  ShieldCheck,
  BookOpen,
  MessageSquare,
  LucideIcon
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { navigation } from '@/config/navigation'
import { getNavigationForRole } from '@/config/role-based-navigation'
import { MenuItem } from '@/types/navigation'
// import { useSession } from '@/lib/auth/mock-auth'
import { UserRole } from '@/lib/rbac/roles'

// Icon mapping
const iconMap: Record<string, LucideIcon> = {
  LayoutDashboard,
  BarChart3,
  FolderKanban,
  Users,
  Bell,
  Settings,
  Sparkles,
  Code,
  CheckSquare,
  UserCheck,
  Building,
  CreditCard,
  Plug,
  Rocket,
  Shield,
  ShieldCheck,
  BookOpen,
  MessageSquare,
}

interface SidebarNavProps {
  isCollapsed?: boolean
}

export function SidebarNav({ isCollapsed = false }: SidebarNavProps) {
  const pathname = useLocation().pathname
  const navigate = useNavigate()
  // const { data: session } = useSession()
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  // Get user role from session, fallback to developer
  // const userRole = (session?.user?.role as UserRole) || UserRole.DEVELOPER
  const userRole = UserRole.ADMIN // Default to admin for now
  const userNavigation = useMemo(() => getNavigationForRole(userRole), [userRole])

  // Auto-expand active item's parent when not collapsed (only one at a time)
  useEffect(() => {
    if (!isCollapsed) {
      let activeParentId: string | null = null

      const findActiveParent = (items: MenuItem[], parentId?: string) => {
        for (const item of items) {
          const isItemActive = item.href === pathname
          const isChildActive = item.href && item.href !== '/' && pathname.startsWith(item.href)

          if (isItemActive || isChildActive) {
            if (parentId) {
              activeParentId = parentId
              return true
            }
            if (item.id && item.children && item.children.length > 0) {
              activeParentId = item.id
              return true
            }
          }

          if (item.children && findActiveParent(item.children, item.id)) {
            return true
          }
        }
        return false
      }

      findActiveParent(userNavigation)

      if (activeParentId) {
        setExpandedItems(new Set([activeParentId]))
      }
    }
  }, [pathname, userNavigation, isCollapsed])

  const toggleExpanded = useCallback((itemId: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (isCollapsed) return

    setExpandedItems(prev => {
      if (prev.has(itemId)) {
        return new Set()
      } else {
        return new Set([itemId])
      }
    })
  }, [isCollapsed])

  const handleItemClick = useCallback((item: MenuItem, e: React.MouseEvent) => {
    const hasChildren = item.children && item.children.length > 0
    
    // If item has href and children, navigate on main click, expand on chevron click
    if (item.href && hasChildren) {
      // Check if click was on the chevron button
      const target = e.target as HTMLElement
      if (target.closest('.expand-button')) {
        e.preventDefault()
        e.stopPropagation()
        toggleExpanded(item.id)
      } else {
        // Navigate to the href
        navigate(item.href)
      }
    } else if (hasChildren) {
      // If only has children (no href), toggle expansion
      e.preventDefault()
      toggleExpanded(item.id)
    } else if (item.href) {
      // If only has href, navigate
      navigate(item.href)
    }
  }, [navigate, toggleExpanded])

  const renderNavItem = useCallback((item: MenuItem, level: number = 0): JSX.Element | null => {
    const isActive = pathname === item.href
    const isParentActive = item.href && item.href !== '/' && pathname.startsWith(item.href) && !isActive
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.has(item.id)
    const Icon = item.icon ? iconMap[item.icon] : null

    // Don't render sub-items when collapsed
    if (isCollapsed && level > 0) return null

    const itemContent = (
      <>
        <div className={cn(
          "flex items-center",
          isCollapsed ? "justify-center w-full" : "flex-1"
        )}>
          {Icon && (
            <Icon 
              className={cn(
                "h-5 w-5 flex-shrink-0",
                !isCollapsed && (level === 0 ? "mr-3" : "mr-2"),
                isActive ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-gray-400"
              )} 
            />
          )}
          {!Icon && level > 0 && !isCollapsed && (
            <Circle 
              className={cn(
                "h-2 w-2 flex-shrink-0 mr-3",
                isActive ? "fill-blue-600 dark:fill-blue-400" : "fill-gray-400 dark:fill-gray-500"
              )} 
            />
          )}
          {!isCollapsed && (
            <>
              <span className={cn(
                "truncate",
                isActive ? "font-semibold" : "font-medium"
              )}>
                {item.label}
              </span>
              {item.badge && (
                <span className={cn(
                  "ml-auto inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                  item.badge === 'Active' 
                    ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                    : typeof item.badge === 'number' && item.badge > 0
                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                    : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
                )}>
                  {item.badge}
                </span>
              )}
            </>
          )}
        </div>
        {!isCollapsed && hasChildren && (
          <button
            onClick={(e) => toggleExpanded(item.id, e)}
            className="expand-button p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded ml-1 transition-colors"
            aria-label="Toggle submenu"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )}
          </button>
        )}
      </>
    )

    const itemClasses = cn(
      "relative flex items-center justify-between rounded-lg text-sm transition-all duration-200 cursor-pointer",
      isCollapsed && level === 0 ? "px-2 py-2" : "px-3 py-2",
      isActive
        ? "bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 dark:from-blue-900/30 dark:to-purple-900/30 dark:text-blue-400 shadow-sm"
        : isParentActive && level === 0
        ? "bg-gray-100 text-gray-900 dark:bg-gray-800/50 dark:text-gray-100"
        : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50",
      !isCollapsed && level === 1 && "ml-9",
      !isCollapsed && level === 2 && "ml-14",
      !isCollapsed && level === 3 && "ml-20",
      "group"
    )

    // Tooltip content for collapsed state
    const tooltipContent = isCollapsed && level === 0 && (
      <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        {item.label}
      </div>
    )

    return (
      <div key={item.id}>
        {/* All items are clickable divs/links */}
        {item.href && !hasChildren ? (
          // Simple link without children
          <Link
            to={item.href}
            className={cn(itemClasses, "relative group")}
          >
            {itemContent}
            {tooltipContent}
          </Link>
        ) : item.href && hasChildren ? (
          // Has both href and children - make the whole item clickable for navigation
          <div
            className={cn(itemClasses, "relative group")}
            onClick={(e) => handleItemClick(item, e)}
          >
            {itemContent}
            {tooltipContent}
          </div>
        ) : hasChildren ? (
          // Only has children, no href - expand/collapse only
          <div
            className={cn(itemClasses, "relative group")}
            onClick={(e) => toggleExpanded(item.id, e)}
          >
            {itemContent}
            {tooltipContent}
          </div>
        ) : (
          // No href and no children - disabled
          <div className={cn(itemClasses, "opacity-50 cursor-not-allowed relative group")}>
            {itemContent}
            {tooltipContent}
          </div>
        )}
        
        {/* Render children with smooth animation */}
        {!isCollapsed && hasChildren && isExpanded && (
          <div className="mt-1 space-y-0.5">
            {item.children!.map(child => renderNavItem(child, level + 1))}
          </div>
        )}
      </div>
    )
  }, [pathname, isCollapsed, expandedItems, handleItemClick, toggleExpanded])

  return (
    <nav className={cn(
      "flex-1 overflow-y-auto overflow-x-hidden",
      isCollapsed ? "px-2 py-2" : "px-3 py-4"
    )}>
      <div className="space-y-1">
        {userNavigation.map(item => renderNavItem(item))}
      </div>
    </nav>
  )
}